import React, {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { Popover, SxProps, ThemeProvider } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import Tooltip from 'components_new/atoms/Tooltip'

import { getTheme } from 'themes'
import DataType from 'types/transformation/enums/DataType'

import { TypeIcon } from './ColumnLabel'
import { BOARD } from './accents'

export interface FieldOption {
  id: string
  name: string
  type?: DataType
  /** Index of the Input node the column originates from (green pill). */
  nodeIndex?: number
  /** Origin table label, when the column traces back to a source table. */
  origin?: string | null
}

interface FieldSelectProps {
  value: string | null
  options: FieldOption[]
  onChange: (id: string) => void
  placeholder?: string
  /** Single-line closed control: origin shown inline, not on a 2nd row. */
  compact?: boolean
  /** When set, the popover shows a row that clears the selection. */
  clearable?: boolean
  onClear?: () => void
  sx?: SxProps
}

const HIT = {
  bg: '#DCFCE7',
  fg: BOARD.signalGreenDark
} as const

/**
 * Persistent marking for the currently-selected row: a green tint plus a left
 * accent bar and a bold name. Kept independent of the hover/keyboard
 * `activeIndex` so the selection stays obvious while another row is active or
 * the list is filtered by a search.
 */
const SELECTED = {
  bg: '#DCFCE7',
  bgActive: '#BBF7D0'
} as const

/** A field can be surfaced by matching either its name or its origin table. */
const matches = (option: FieldOption, query: string) =>
  option.name.toLowerCase().includes(query) ||
  (option.origin ?? '').toLowerCase().includes(query)

/** Split a name around the query match for highlighting (name matches only). */
const splitMatch = (name: string, query: string) => {
  const index = query ? name.toLowerCase().indexOf(query) : -1

  if (index < 0) {
    return { pre: name, hit: '', post: '' }
  }

  return {
    pre: name.slice(0, index),
    hit: name.slice(index, index + query.length),
    post: name.slice(index + query.length)
  }
}

/** Small green pill showing the origin Input-node index. */
const NodePill = (props: { index: number }) => (
  <Box
    component="span"
    sx={{
      alignItems: 'center',
      backgroundColor: BOARD.signalGreen,
      borderRadius: '4px',
      color: 'common.white',
      display: 'inline-grid',
      flex: 'none',
      fontFamily: BOARD.fontBody,
      fontSize: '9px',
      fontWeight: 700,
      height: '14px',
      minWidth: '14px',
      placeItems: 'center',
      px: '3px'
    }}
  >
    {props.index}
  </Box>
)

/**
 * Standard field (column) selector reused by every non-Input board node.
 *
 * Closed: a soft-grey row with the column's type icon, its name, and a subtitle
 * showing the origin table + Input-node number (the same source can feed
 * several Input nodes, so the number disambiguates). Open: a popover that
 * searches by column *or* table name, groups results by origin table + node,
 * highlights the matched substring, and marks the current selection.
 * Keyboard: ↑/↓ move, Enter selects, Esc closes.
 */
const FieldSelect = (props: FieldSelectProps) => {
  const {
    value,
    options,
    onChange,
    placeholder = 'Välj fält',
    compact = true,
    clearable = false,
    onClear,
    sx
  } = props

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  const listRef = useRef<HTMLDivElement | null>(null)

  const open = Boolean(anchorEl)
  const query = searchQuery.trim().toLowerCase()

  const reduceMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const selected = useMemo(
    () => options.find((option) => option.id === value) ?? null,
    [options, value]
  )

  // Filtered options, grouped by origin table + node, order preserved. Also a
  // flat list of ids for keyboard navigation across the whole result set.
  const { groups, flatIds } = useMemo(() => {
    const filtered = query
      ? options.filter((option) => matches(option, query))
      : options

    const byKey = new Map<string, FieldOption[]>()

    for (const option of filtered) {
      const key = `${option.origin ?? ''}|${option.nodeIndex ?? ''}`
      const bucket = byKey.get(key)

      if (bucket) {
        bucket.push(option)
      } else {
        byKey.set(key, [option])
      }
    }

    return {
      groups: Array.from(byKey.values()).map((fields) => ({
        origin: fields[0].origin,
        nodeIndex: fields[0].nodeIndex,
        fields
      })),
      flatIds: filtered.map((option) => option.id)
    }
  }, [options, query])

  useEffect(() => {
    setActiveIndex(0)
  }, [query, open])

  useEffect(() => {
    const row = listRef.current?.querySelector<HTMLElement>(
      `[data-row-index="${activeIndex}"]`
    )

    row?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setSearchQuery('')
  }

  const handleClose = () => {
    setAnchorEl(null)
    setSearchQuery('')
  }

  const handlePick = (id: string) => {
    onChange(id)
    handleClose()
  }

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActiveIndex((index) => Math.min(index + 1, flatIds.length - 1))
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActiveIndex((index) => Math.max(index - 1, 0))
      } else if (event.key === 'Enter') {
        event.preventDefault()
        const id = flatIds[activeIndex]

        if (id) {
          handlePick(id)
        }
      } else if (event.key === 'Escape') {
        event.preventDefault()
        handleClose()
      }
    },
    [flatIds, activeIndex]
  )

  // Running index across groups so keyboard highlight and rendering agree.
  let rowIndex = -1

  return (
    <>
      <Box
        className="nodrag ignore-locked-mode"
        onClick={handleOpen}
        sx={{
          alignItems: 'center',
          backgroundColor: BOARD.rowBackground,
          border: '1px solid transparent',
          borderRadius: '10px',
          cursor: 'pointer',
          display: 'flex',
          gap: 1,
          p: '7px 10px',
          '&:hover': { backgroundColor: '#F0F0F2' },
          ...sx
        }}
      >
        {selected ? (
          <TypeIcon type={selected.type} />
        ) : (
          <Box
            sx={{
              backgroundColor: 'common.white',
              border: `1px solid ${BOARD.border}`,
              borderRadius: '5px',
              flex: 'none',
              height: '19px',
              width: '19px'
            }}
          />
        )}
        <Box
          sx={{
            alignItems: compact ? 'center' : 'stretch',
            display: 'flex',
            flex: 1,
            flexDirection: compact ? 'row' : 'column',
            gap: compact ? '7px' : 0,
            minWidth: 0
          }}
        >
          <Tooltip title={selected ? selected.name : ''} placement="top">
            <Box
              sx={{
                color: selected ? BOARD.textPrimary : BOARD.textFaint,
                flex: compact ? 1 : undefined,
                fontFamily: BOARD.fontBody,
                fontSize: '12.5px',
                fontStyle: selected ? 'normal' : 'italic',
                fontWeight: 500,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {selected ? selected.name : placeholder}
            </Box>
          </Tooltip>
          {selected && (selected.origin || selected.nodeIndex) ? (
            <Box
              sx={{
                alignItems: 'center',
                color: BOARD.textFaint,
                display: 'flex',
                flex: 'none',
                fontFamily: BOARD.fontBody,
                fontSize: '10.5px',
                gap: '5px',
                maxWidth: compact ? '55%' : 'none',
                minWidth: 0
              }}
            >
              {selected.origin ? (
                <Box
                  component="span"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {selected.origin}
                </Box>
              ) : null}
              {selected.nodeIndex ? (
                <NodePill index={selected.nodeIndex} />
              ) : null}
            </Box>
          ) : null}
        </Box>
        <Icon
          name="ExpandMore"
          sx={{ color: BOARD.textFaint, flex: 'none', fontSize: '15px' }}
        />
      </Box>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        disableScrollLock={true}
        onClose={handleClose}
        onMouseDown={(event) => event.stopPropagation()}
        open={open}
        transitionDuration={reduceMotion ? 0 : undefined}
        slotProps={{
          paper: {
            sx: {
              border: `1px solid ${BOARD.border}`,
              borderRadius: '13px',
              boxShadow: '0 14px 34px rgba(16, 24, 40, 0.16)',
              mt: '6px',
              overflow: 'hidden',
              width: 300
            }
          }
        }}
      >
        <ThemeProvider theme={getTheme('light')}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', maxHeight: 300 }}
          >
            <Box
              sx={{
                borderBottom: `1px solid ${BOARD.borderSoft}`,
                p: '9px 10px 7px'
              }}
            >
              <Box
                sx={{
                  alignItems: 'center',
                  backgroundColor: BOARD.chipBackground,
                  borderRadius: '9px',
                  display: 'flex',
                  gap: 1,
                  p: '6px 9px'
                }}
              >
                <Icon
                  name="Search"
                  sx={{ color: BOARD.textFaint, fontSize: '15px' }}
                />
                <input
                  autoFocus={true}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Sök fält…"
                  value={searchQuery}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: BOARD.textPrimary,
                    flex: 1,
                    fontFamily: BOARD.fontBody,
                    fontSize: '13px',
                    minWidth: 0,
                    outline: 'none',
                    padding: 0
                  }}
                />
              </Box>
            </Box>

            <Box ref={listRef} sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
              {clearable && !query ? (
                <Box
                  role="button"
                  onClick={() => {
                    onClear?.()
                    handleClose()
                  }}
                  sx={{
                    alignItems: 'center',
                    backgroundColor:
                      value === null ? SELECTED.bg : 'transparent',
                    borderLeft: `3px solid ${
                      value === null ? BOARD.signalGreen : 'transparent'
                    }`,
                    color: BOARD.textMuted,
                    cursor: 'pointer',
                    display: 'flex',
                    fontFamily: BOARD.fontBody,
                    fontSize: '13px',
                    fontStyle: 'italic',
                    fontWeight: value === null ? 700 : 400,
                    gap: 1,
                    p: '7px 12px 7px 9px',
                    '&:hover': {
                      backgroundColor:
                        value === null
                          ? SELECTED.bgActive
                          : BOARD.chipBackground
                    }
                  }}
                >
                  <Icon name="Close" sx={{ fontSize: '15px' }} />
                  Ej mappad
                  {value === null ? (
                    <Icon
                      name="Check"
                      sx={{
                        color: BOARD.signalGreen,
                        marginLeft: 'auto',
                        fontSize: '16px'
                      }}
                    />
                  ) : null}
                </Box>
              ) : null}

              {flatIds.length === 0 ? (
                <Box
                  sx={{
                    color: BOARD.textFaint,
                    fontFamily: BOARD.fontBody,
                    fontSize: '12.5px',
                    p: '14px 8px',
                    textAlign: 'center'
                  }}
                >
                  Inga fält matchar
                </Box>
              ) : null}

              {groups.map((group) => (
                <Box key={`${group.origin ?? ''}|${group.nodeIndex ?? ''}`}>
                  {group.origin || group.nodeIndex ? (
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: '5px',
                        p: '7px 12px 3px'
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          color: BOARD.textFaint,
                          fontFamily: BOARD.fontDisplay,
                          fontSize: '10px',
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {group.origin ?? 'Okänd källa'}
                      </Box>
                      {group.nodeIndex ? (
                        <NodePill index={group.nodeIndex} />
                      ) : null}
                    </Box>
                  ) : null}
                  {group.fields.map((option) => {
                    rowIndex += 1
                    const currentIndex = rowIndex
                    const { pre, hit, post } = splitMatch(option.name, query)
                    const isSelected = option.id === value
                    const isActive = activeIndex === currentIndex

                    return (
                      <Box
                        data-row-index={currentIndex}
                        key={option.id}
                        onClick={() => handlePick(option.id)}
                        onMouseEnter={() => setActiveIndex(currentIndex)}
                        sx={{
                          alignItems: 'center',
                          backgroundColor: isSelected
                            ? isActive
                              ? SELECTED.bgActive
                              : SELECTED.bg
                            : isActive
                              ? BOARD.chipBackground
                              : 'transparent',
                          borderLeft: `3px solid ${
                            isSelected ? BOARD.signalGreen : 'transparent'
                          }`,
                          cursor: 'pointer',
                          display: 'flex',
                          gap: 1,
                          p: '7px 12px 7px 9px'
                        }}
                      >
                        <TypeIcon type={option.type} />
                        <Tooltip title={option.name} placement="top">
                          <Box
                            sx={{
                              color: BOARD.textPrimary,
                              flex: 1,
                              fontFamily: BOARD.fontBody,
                              fontSize: '13px',
                              fontWeight: isSelected ? 700 : 400,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {pre}
                            {hit ? (
                              <Box
                                component="span"
                                sx={{
                                  backgroundColor: HIT.bg,
                                  borderRadius: '3px',
                                  color: HIT.fg,
                                  fontWeight: 700
                                }}
                              >
                                {hit}
                              </Box>
                            ) : null}
                            {post}
                          </Box>
                        </Tooltip>
                        {isSelected ? (
                          <Icon
                            name="Check"
                            sx={{
                              color: BOARD.signalGreen,
                              flex: 'none',
                              fontSize: '16px'
                            }}
                          />
                        ) : null}
                      </Box>
                    )
                  })}
                </Box>
              ))}
            </Box>
          </Box>
        </ThemeProvider>
      </Popover>
    </>
  )
}

export default FieldSelect
